import React, { useId } from 'react';

export interface FieldProps {
  label: React.ReactNode;
  /** A single control: Input, Select, Textarea, Combobox or a native element. */
  children: React.ReactElement<Record<string, unknown>>;
  help?: React.ReactNode;
  /** Replaces the help text and marks the control invalid. */
  error?: React.ReactNode;
  required?: boolean;
  className?: string;
}

/** A label, a control, and its help or error text, wired together for assistive tech. */
const Field: React.FC<FieldProps> = ({ label, children, help, error, required, className }) => {
  const generated = useId();
  const controlId = (children.props.id as string | undefined) ?? generated;
  const noteId = `${controlId}-note`;
  const note = error ?? help;
  return (
    <div className={['ui-field', className || ''].filter(Boolean).join(' ')}>
      <label className="ui-label" htmlFor={controlId}>
        {label}
        {required && (
          <span className="ui-required" aria-hidden="true">
            {' '}
            *
          </span>
        )}
      </label>
      {React.cloneElement(children, {
        id: controlId,
        required: required || children.props.required,
        'aria-describedby': note ? noteId : children.props['aria-describedby'],
        'aria-invalid': error ? true : children.props['aria-invalid'],
      })}
      {note && (
        <span id={noteId} className={error ? 'ui-error' : 'ui-help'}>
          {note}
        </span>
      )}
    </div>
  );
};

export default Field;
